import React, { FC, useState } from 'react';
import { Row, Button, Modal, Alert } from 'antd';
import { CloudDownloadOutlined, PlusCircleFilled, WarningOutlined } from '@ant-design/icons';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { FLAT_FILE_PUBLISHABLE_KEY, WARNING_BUTTON_WAIT_TIME } from '../../utils/DataConstants';
import ButtonWithTimer from '../common/ButtonWithTimer';
import FlatfileButton from './FlatfileButton';
import { FlatfileProvider } from '@flatfile/react';

interface UploadWithSampleModalProps {
	type: string;
	entityName: string;
	title: string;
	onCancel: () => void;
	onDownloadSample: () => void;
	downloading: boolean;
	fields: any[];
	onDataLoad: () => void;
	sampleFileText: string;
	downloadText: string;
	showWarningMessage: boolean;
	mappingConfidenceThreshold?: boolean;
	recordHook?: (x: any) => any;
}

const UploadWithSampleModal: FC<UploadWithSampleModalProps> = ({
	type,
	entityName,
	title,
	fields,
	downloading,
	sampleFileText,
	downloadText,
	showWarningMessage,
	mappingConfidenceThreshold,
	onDataLoad,
	recordHook,
	onCancel,
	onDownloadSample,
}): React.ReactElement => {
	const [visible, setVisible] = useState(true);

	const onOpenPortal = (launch) => () => {
		setVisible(false);
		launch();
	};

	return (
		<FlatfileProvider
			publishableKey={FLAT_FILE_PUBLISHABLE_KEY}
			config={{
				preload: false,
			}}
		>
			<Modal visible={visible} title={title} closable onCancel={onCancel} forceRender footer={null}>
				<Row>{downloadText || 'Do you want to download a sample file?'}</Row>
				{showWarningMessage ? (
					<>
						<div className="mt-2">
							<Alert message="You will lose data if you do not read this carefully." type="error" />
						</div>
						<div className="mt-4 flex flex-row items-center">
							<WarningOutlined />
							<div className="ml-2 text-gray-400 ">
								{`Please ensure that you always upload the entire ${
									entityName || type
								} list. You can download the list here to make updates, addition/deletion.`}
							</div>
						</div>
					</>
				) : null}
				<Row>
					<Button
						type="primary"
						style={{ marginTop: '24px', width: '100%' }}
						loading={downloading}
						onClick={onDownloadSample}
					>
						<CloudDownloadOutlined />
						{sampleFileText || 'Download Sample File'}
					</Button>
				</Row>
				<Row>
					<FlatfileButton
						type={type}
						fields={fields}
						onDataLoad={onDataLoad}
						recordHook={recordHook}
						onCancel={onCancel}
						mappingConfidenceThreshold={mappingConfidenceThreshold}
						renderElement={(launch) =>
							showWarningMessage ? (
								<ButtonWithTimer
									icon={<PlusCircleFilled />}
									style={{ marginTop: '12px', width: '100%' }}
									disableUntilSeconds={WARNING_BUTTON_WAIT_TIME}
									onClick={onOpenPortal(launch)}
								>
									Proceed to Upload
								</ButtonWithTimer>
							) : (
								<Button
									icon={<PlusCircleFilled />}
									style={{ marginTop: '12px', width: '100%' }}
									onClick={onOpenPortal(launch)}
								>
									Proceed to Upload
								</Button>
							)
						}
					/>
				</Row>
			</Modal>
		</FlatfileProvider>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

export default withRouter<any, any>(connect(mapStateToProps)(UploadWithSampleModal));
